import { useState, useEffect, useMemo } from 'react'
import {
  useChainId,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
} from 'wagmi'
import { encodeFunctionData, maxUint256, parseUnits } from 'viem'
import { sendGTMEvent } from '@next/third-parties/google'
import { ConnectorName, useChainSpecificWallet } from 'wallet'
import { isFeatureSupported } from 'wallet/config/gasSponsorship'
import thalesContract from '@/Overtime/utils/contracts/thalesContract'
import multipleCollateralContract from '@/Overtime/utils/contracts/multipleCollateralContract'
import sportsAMMV2Contract from '@/Overtime/utils/contracts/sportsAMMV2Contract'

interface UseAllowanceProps {
  address: string
  betAmount: string
  chain: any
  connector: any
}

export const useAllowance = ({
  address,
  betAmount,
  chain,
  connector,
}: UseAllowanceProps) => {
  const [isAllowanceInitiated, setIsAllowanceInitiated] = useState(false)
  const [isApprovePending, setIsApprovePending] = useState(false)
  const [wasApprovePending, setWasApprovePending] = useState(false)
  const chainId = useChainId()
  const [gaslessError, setGaslessError] = useState<string | null>(null)

  // Get chain-specific wallet functionality
  const chainSpecificWallet = useChainSpecificWallet()
  const {
    hasGasSponsorship,
    executeGaslessTransaction,
    isReady: isGaslessReady,
    gaslessType,
  } = chainSpecificWallet

  // Check if we can use gasless transactions for approvals on this chain
  const canUseGasless = useMemo(() => {
    return (
      chainId &&
      isFeatureSupported(chainId, 'sponsorApprovals') &&
      hasGasSponsorship &&
      isGaslessReady
    )
  }, [chainId, hasGasSponsorship, isGaslessReady])

  const { data: allowanceData, refetch: refetchAllowance } = useReadContract({
    address: chain?.id
      ? (multipleCollateralContract.USDC.addresses[chain.id] as `0x${string}`)
      : undefined,
    abi: thalesContract.abi,
    functionName: 'allowance',
    args: chain?.id
      ? [address, sportsAMMV2Contract.addresses[chain.id]]
      : undefined,
    query: {
      enabled: !!chain?.id && !!address,
    },
  })

  const {
    writeContract,
    data: approveData,
    error: writeError,
    isPending: isWritePending,
  } = useWriteContract()

  const { isLoading: isApproveLoading, isSuccess: isApproveSuccess } =
    useWaitForTransactionReceipt({
      hash: approveData as `0x${string}`,
    })

  const isApprovePendingCombined =
    isApprovePending || isWritePending || isApproveLoading

  useEffect(() => {
    if (writeError) {
      console.error('Write contract error:', writeError)
      setIsApprovePending(false)
      setIsAllowanceInitiated(false)
    }
  }, [writeError])

  useEffect(() => {
    if (isApproveSuccess) {
      setIsApprovePending(false)
      setIsAllowanceInitiated(false)
      setWasApprovePending(true)
      refetchAllowance()
    }
  }, [isApproveSuccess, refetchAllowance])

  const allowanceFormatted = useMemo(() => {
    if (!allowanceData) {
      return null
    }
    return allowanceData.toString()
  }, [allowanceData])

  const isApproveRequired = useMemo(() => {
    if (!allowanceFormatted || !betAmount) {
      return false
    }

    try {
      // Convert decimal betAmount to proper token unit (USDC typically has 6 decimals)
      const betAmountWei = parseUnits(betAmount, 6)
      return BigInt(allowanceFormatted) < betAmountWei
    } catch (error) {
      console.error('Error converting betAmount to BigInt:', error)
      return false
    }
  }, [allowanceFormatted, betAmount])

  const handleApprove = async () => {
    if (!chainId) {
      return
    }

    setIsApprovePending(true)
    setIsAllowanceInitiated(true)
    setGaslessError(null)

    try {
      const tokenAddress = multipleCollateralContract.USDC.addresses[
        chainId
      ] as `0x${string}`
      const spenderAddress = sportsAMMV2Contract.addresses[
        chainId
      ] as `0x${string}`

      // Try gasless transaction first if available
      if (canUseGasless && executeGaslessTransaction) {
        try {
          // Encode the approve function call
          const approveData = encodeFunctionData({
            abi: thalesContract.abi,
            functionName: 'approve',
            args: [spenderAddress, maxUint256],
          })

          const txHash = await executeGaslessTransaction(
            tokenAddress,
            approveData,
            BigInt(0)
          )

          // Refetch allowance after successful transaction
          setTimeout(() => {
            refetchAllowance()
          }, 2000)

          setIsApprovePending(false)
          setIsAllowanceInitiated(false)
          return
        } catch (gaslessError) {
          setGaslessError(
            gaslessError instanceof Error
              ? gaslessError.message
              : 'Gasless transaction failed'
          )
          // Continue to regular transaction fallback below
        }
      }

      // Regular transaction (fallback or when gasless is not available)
      writeContract({
        address: tokenAddress,
        abi: thalesContract.abi,
        functionName: 'approve',
        args: [spenderAddress, maxUint256],
      })

      refetchAllowance()
    } catch (error) {
      setIsApprovePending(false)
      setIsAllowanceInitiated(false)
    }
  }

  const handleAllowanceEvent = (isProcessing: boolean) => {
    if (isProcessing) {
      sendGTMEvent({
        event: 'spend_allowance_approved',
        destination_chain: chain?.name,
        destination_wallet_address: address,
        wallet_app_name:
          connector?.name === ConnectorName.Privy ? 'privy' : connector?.name,
      })
      setIsAllowanceInitiated(false)
    } else {
      sendGTMEvent({
        event: 'spend_allowance_rejected',
        destination_chain: chain?.name,
        destination_wallet_address: address,
        wallet_app_name:
          connector?.name === ConnectorName.Privy ? 'privy' : connector?.name,
      })
    }
  }

  return {
    isApproveRequired,
    isAllowanceInitiated,
    isApprovePending: isApprovePendingCombined,
    handleApprove,
    handleAllowanceEvent,
    refetchAllowance,
    allowanceData,
    // Expose gasless transaction info
    hasGasSponsorship,
    canUseGasless,
    gaslessError,
    gaslessType,
    // Chain-specific info
    chainSupportsApprovalSponsorship: chainId
      ? isFeatureSupported(chainId, 'sponsorApprovals')
      : false,
  }
}
